import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { sendNativeBoardBackground } from 'webapp/utils/native-bridge';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DatawallWallsSliderComponent from 'webapp/components/datawall/walls-slider';

import type Board from 'webapp/models-custom/board';
import type BoardRecord from 'webapp/models/board';

interface Args {
	model: BoardRecord;
}

export default class MobileBoardRouteTemplate extends Component<Args> {
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-previews')
	declare mmBoardPreviewsService: Service.ModelManager.BoardPreviews;

	@tracked previewHash = '';

	get originalBoard(): Board {
		return this.mmBoardsService.createModel(this.args.model);
	}

	get board(): Board {
		let board;
		if (this.previewHash) {
			board = this.mmBoardPreviewsService.findModelByHash(this.previewHash);
		}
		return board || this.originalBoard;
	}

	get boardsArray(): Board[] {
		const board = this.board,
			boardsArray = [];
		if (board) {
			boardsArray.push(board);
		}
		return boardsArray;
	}

	resetPreviewToOriginal(): void {
		const board = this.board;

		if (board.isPreview) {
			const boardId = board.id!;
			this.mmBoardsService.markBoardNotRequested(boardId);
			void this.mmBoardsService.requestBoardData(boardId);
		}
	}

	@action
	boardStatusHashChanged(_boardId: string, previewHash?: string | null): void {
		this.previewHash = previewHash || '';
		if (!previewHash) {
			this.resetPreviewToOriginal();
		}
	}

	@action
	reportBgTheme(): void {
		sendNativeBoardBackground(this.board.bgTheme);
	}

	@action
	onSlideChange(_index: number): void {
		// no slide changing here
	}

	@action
	toggleFullscreen(): void {
		// no fullscreen here
	}

	<template>
		{{#if this.boardsArray}}

			<div
				id="datawall"
				{{didInsert this.reportBgTheme}}
				{{didUpdate this.reportBgTheme this.board.bgTheme}}
			>

				<DatawallWallsSliderComponent
					@model={{this.boardsArray}}
					@slideIndex={{0}}
					@loopDelay={{0}}
					@hasHeader={{false}}
					@hasFooter={{false}}
					@isTemplate={{false}}
					@isInIframe={{false}}
					@showDesignerLink={{false}}
					@skipActions={{true}}
					@onBoardPreviewHashChange={{this.boardStatusHashChanged}}
					@onSlideChange={{this.onSlideChange}}
					@onToggleFullscreen={{this.toggleFullscreen}}
				/>

			</div>

		{{else}}

			<div class="list-empty">
				<div class="header">Dashboards</div>
				<p class="error-message">
					<CommonUiIconComponent @name="AlertFill" @size="sm" />
					Cannot load dashboard
				</p>
			</div>

		{{/if}}
	</template>
}
